import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Stepper,
  Step,
  StepLabel,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Button,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';

import apiClient from '../services/api';
import onboardingService from '../services/onboardingService';
import { updateUserProfile } from '../slices/authSlice';
import { addDebt } from '../slices/debtSlice';
import { detectDefaultCurrency, supportedCurrencies } from '../utils/currencyUtils';
import goldColors from '../themes/goldColors';
import { useLanguage } from '../contexts/LanguageContext';

// Reconstrucción completa (31 jul 2026) del asistente "llévame de la mano"
// para clientes nuevos. Reemplaza a FinancialOnboarding.js + el uso
// embebido de AddBankAccountForm.js, que tenían varios bugs reales:
//   1. El botón "Cancelar" mandaba a http://localhost:3000/welcome —
//      quemado desde desarrollo, roto en producción.
//   2. AddBankAccountForm se renderizaba como un <Dialog> DENTRO del
//      <Dialog> de FinancialOnboarding — dos modales encimados.
//   3. El progreso se guardaba en un estado propio (Redux
//      state.auth.onboarding + varias claves de localStorage) totalmente
//      separado del OnboardingState real que ya usa OnboardingSidebar.js
//      para el checklist de la barra lateral — cargar el wizard nunca
//      reflejaba en qué paso el cliente realmente se había quedado según
//      el servidor.
// Este componente es un solo Dialog (sin anidar), sin estado propio de
// progreso: en cada paso escribe directo contra los mismos endpoints reales
// que ya usan CuentasPage.js/DebtsPage.js/UserProfile.js, y al abrir lee
// /onboarding/data (la misma fuente que usa la barra lateral) para saber en
// qué paso continuar.
//
// 17 sep 2026 (pedido de Augusto: "se puede tener una version completa en
// ingles?"): tipoDeuda (tipo/label) sigue el mismo criterio que
// DebtsPage.js -- el VALUE que se guarda queda fijo, solo el label
// traducido; bancosSugeridos son nombres propios reales de bancos, no se
// traducen.
const OnboardingWizard = ({ open, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const defaultCurrency = useMemo(() => detectDefaultCurrency(user?.country), [user?.country]);
  const { language } = useLanguage();
  const isSpanish = language === 'es';

  const steps = isSpanish ? ['Perfil', 'Cuentas', 'Deudas'] : ['Profile', 'Accounts', 'Debts'];

  const [loadingInitial, setLoadingInitial] = useState(true);
  const [step, setStep] = useState(0);
  const [error, setError] = useState(null);
  // Progreso ya existente al abrir el asistente (ver useEffect de abajo) —
  // permite avanzar en Cuentas/Deudas aunque el cliente no agregue nada
  // nuevo en esta sesión del wizard, si esos pasos ya estaban completos
  // desde antes (ej. reabrió el asistente después de haber agregado una
  // cuenta directamente desde /accounts).
  const [initialData, setInitialData] = useState(null);

  // --- Perfil ---
  const [perfil, setPerfil] = useState({
    occupation: '',
    employmentType: 'employee',
    maritalStatus: '',
    hasDependents: 'no',
    dependentsCount: '',
  });
  const [savingPerfil, setSavingPerfil] = useState(false);

  // --- Cuentas ---
  const bancosSugeridos = [
    'Banco de Crédito del Perú (BCP)', 'BBVA Perú', 'Interbank', 'Scotiabank Perú',
    'Banco Pichincha', 'Banco de la Nación', 'MiBanco', 'Banco Falabella', 'Banco Ripley',
    'Bancolombia', 'BBVA México', 'Santander', 'Citibank', 'Wells Fargo', 'Chase',
  ];
  const monedaOptions = ['USD', 'PEN', ...Object.keys(supportedCurrencies).filter((c) => c !== 'USD' && c !== 'PEN')];
  const emptyCuenta = () => ({ nombre: '', entidad: '', tipo: 'ahorros', moneda: defaultCurrency, saldo: '' });
  const [nuevaCuenta, setNuevaCuenta] = useState(emptyCuenta());
  const [cuentasAgregadas, setCuentasAgregadas] = useState([]);
  const [savingCuenta, setSavingCuenta] = useState(false);

  // --- Deudas ---
  const tiposDeuda = [
    { value: 'tarjeta', label: isSpanish ? 'Tarjeta de crédito' : 'Credit card' },
    { value: 'prestamo_personal', label: isSpanish ? 'Préstamo personal' : 'Personal loan' },
    { value: 'prestamo_estudiantil', label: isSpanish ? 'Préstamo estudiantil' : 'Student loan' },
    { value: 'hipoteca', label: isSpanish ? 'Hipoteca' : 'Mortgage' },
    { value: 'otro', label: isSpanish ? 'Otro' : 'Other' },
  ];
  const emptyDeuda = () => ({ nombre: '', tipo: 'tarjeta', monto: '', moneda: defaultCurrency, tasa_interes: '', plazo_meses: '' });
  const [nuevaDeuda, setNuevaDeuda] = useState(emptyDeuda());
  const [deudasAgregadas, setDeudasAgregadas] = useState([]);
  const [savingDeuda, setSavingDeuda] = useState(false);
  const [marcandoSinDeudas, setMarcandoSinDeudas] = useState(false);
  const [sinDeudas, setSinDeudas] = useState(false);

  // Cuota estimada en vivo (misma fórmula que DebtsPage.js) — puramente
  // informativa mientras el cliente escribe.
  const cuotaEstimada = useMemo(() => {
    const P = Number(nuevaDeuda.monto) || 0;
    const n = parseInt(nuevaDeuda.plazo_meses, 10) || 0;
    if (!P || !n) return null;
    const rMensual = (Number(nuevaDeuda.tasa_interes) || 0) / 100 / 12;
    if (rMensual === 0) return P / n;
    const factor = Math.pow(1 + rMensual, -n);
    return (P * rMensual) / (1 - factor);
  }, [nuevaDeuda.monto, nuevaDeuda.tasa_interes, nuevaDeuda.plazo_meses]);

  // Al abrir el asistente, se lee el progreso real (mismo endpoint que
  // OnboardingSidebar.js) para arrancar en el primer paso que de verdad
  // falta — no siempre en "Perfil". Si los tres ya están completos, se
  // salta directo al cierre (no debería llegar a abrirse en ese caso, pero
  // por si acaso no deja al cliente atascado).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingInitial(true);
    setError(null);
    onboardingService
      .getOnboardingData()
      .then((data) => {
        if (cancelled) return;
        setInitialData(data);
        if (!data?.profile_completed) setStep(0);
        else if (!data?.accounts_completed) setStep(1);
        else if (!data?.debts_completed) setStep(2);
        else {
          // Ya está todo completo — cerrar de una vez.
          if (onClose) onClose();
        }
      })
      .catch(() => {
        // Si falla la lectura, arrancar desde Perfil de todas formas —
        // mejor eso que dejar al cliente sin poder abrir el asistente.
        if (!cancelled) setStep(0);
      })
      .finally(() => {
        if (!cancelled) setLoadingInitial(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleGuardarPerfil = async () => {
    if (!perfil.occupation.trim() || !perfil.maritalStatus) {
      setError(isSpanish ? 'Completa ocupación y estado civil para continuar.' : 'Fill in occupation and marital status to continue.');
      return;
    }
    setError(null);
    setSavingPerfil(true);
    try {
      await dispatch(updateUserProfile({
        occupation: perfil.occupation.trim(),
        employmentType: perfil.employmentType,
        maritalStatus: perfil.maritalStatus,
        hasDependents: perfil.hasDependents,
        dependentsCount: perfil.dependentsCount,
      })).unwrap();
      setStep(1);
    } catch (err) {
      setError(err?.detail || err?.message || (isSpanish ? 'No se pudo guardar el perfil. Intenta de nuevo.' : 'Could not save the profile. Please try again.'));
    } finally {
      setSavingPerfil(false);
    }
  };

  const handleAgregarCuenta = async () => {
    if (!nuevaCuenta.nombre.trim() || !nuevaCuenta.entidad.trim()) {
      setError(isSpanish ? 'El nombre de la cuenta y el banco son obligatorios.' : 'The account name and bank are required.');
      return;
    }
    setError(null);
    setSavingCuenta(true);
    try {
      const payload = {
        nombre: nuevaCuenta.nombre.trim(),
        tipo: nuevaCuenta.tipo,
        entidad: nuevaCuenta.entidad.trim(),
        saldo: parseFloat(nuevaCuenta.saldo) || 0,
        moneda: nuevaCuenta.moneda,
      };
      const response = await apiClient.post('/dashboard/accounts', payload);
      setCuentasAgregadas((prev) => [...prev, response.data]);
      setNuevaCuenta(emptyCuenta());
    } catch (err) {
      setError(isSpanish ? 'No se pudo guardar la cuenta. Intenta de nuevo.' : 'Could not save the account. Please try again.');
    } finally {
      setSavingCuenta(false);
    }
  };

  const handleSiguienteCuentas = async () => {
    // accounts_completed ya se recalcula solo en el servidor a partir de
    // si el cliente tiene cuentas reales (ver /onboarding/data en el
    // backend) — no hace falta marcarlo a mano, ya quedó reflejado con la
    // primera cuenta creada arriba.
    setStep(2);
  };

  const handleAgregarDeuda = async () => {
    if (!nuevaDeuda.nombre.trim()) {
      setError(isSpanish ? 'El nombre de la deuda es obligatorio.' : 'The debt name is required.');
      return;
    }
    setError(null);
    setSavingDeuda(true);
    try {
      const payload = {
        nombre: nuevaDeuda.nombre.trim(),
        tipo: nuevaDeuda.tipo,
        monto: parseFloat(nuevaDeuda.monto) || 0,
        moneda: nuevaDeuda.moneda,
        tasa_interes: nuevaDeuda.tasa_interes === '' ? null : parseFloat(nuevaDeuda.tasa_interes),
        plazo_meses: nuevaDeuda.plazo_meses === '' ? null : parseInt(nuevaDeuda.plazo_meses, 10),
      };
      const created = await dispatch(addDebt(payload)).unwrap();
      setDeudasAgregadas((prev) => [...prev, created]);
      setNuevaDeuda(emptyDeuda());
    } catch (err) {
      setError(isSpanish ? 'No se pudo guardar la deuda. Intenta de nuevo.' : 'Could not save the debt. Please try again.');
    } finally {
      setSavingDeuda(false);
    }
  };

  const handleNoTengoDeudas = async () => {
    setMarcandoSinDeudas(true);
    setError(null);
    try {
      await onboardingService.updateOnboardingState({ progress: { debts: true } });
      setSinDeudas(true);
      setStep(3);
    } catch (err) {
      setError(isSpanish ? 'No se pudo continuar. Intenta de nuevo.' : 'Could not continue. Please try again.');
    } finally {
      setMarcandoSinDeudas(false);
    }
  };

  const handleFinalizarDeudas = () => setStep(3);

  const handleIrAlTablero = () => {
    if (onClose) onClose();
    navigate('/dashboard');
  };

  const renderPerfil = () => (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {isSpanish ? 'Un par de datos rápidos para adaptar la app a tu situación.' : 'A couple of quick details to tailor the app to your situation.'}
      </Typography>
      <TextField
        fullWidth
        label={isSpanish ? 'Ocupación' : 'Occupation'}
        placeholder={isSpanish ? 'Ej: Contador, dueño de restaurante, ingeniero...' : 'E.g. Accountant, restaurant owner, engineer...'}
        value={perfil.occupation}
        onChange={(e) => setPerfil((p) => ({ ...p, occupation: e.target.value }))}
        sx={{ mb: 2.5 }}
      />
      <FormControl fullWidth sx={{ mb: 2.5 }}>
        <InputLabel>{isSpanish ? 'Situación laboral' : 'Employment status'}</InputLabel>
        <Select
          label={isSpanish ? 'Situación laboral' : 'Employment status'}
          value={perfil.employmentType}
          onChange={(e) => setPerfil((p) => ({ ...p, employmentType: e.target.value }))}
        >
          <MenuItem value="employee">{isSpanish ? 'Asalariado' : 'Employee'}</MenuItem>
          <MenuItem value="selfEmployed">{isSpanish ? 'Independiente' : 'Self-employed'}</MenuItem>
          <MenuItem value="both">{isSpanish ? 'Ambos' : 'Both'}</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth sx={{ mb: 2.5 }}>
        <InputLabel>{isSpanish ? 'Estado civil' : 'Marital status'}</InputLabel>
        <Select
          label={isSpanish ? 'Estado civil' : 'Marital status'}
          value={perfil.maritalStatus}
          onChange={(e) => setPerfil((p) => ({ ...p, maritalStatus: e.target.value }))}
        >
          <MenuItem value="single">{isSpanish ? 'Soltero/a' : 'Single'}</MenuItem>
          <MenuItem value="married">{isSpanish ? 'Casado/a' : 'Married'}</MenuItem>
          <MenuItem value="divorced">{isSpanish ? 'Divorciado/a' : 'Divorced'}</MenuItem>
          <MenuItem value="widowed">{isSpanish ? 'Viudo/a' : 'Widowed'}</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth sx={{ mb: perfil.hasDependents === 'yes' ? 2.5 : 0 }}>
        <InputLabel>{isSpanish ? '¿Tienes dependientes?' : 'Do you have dependents?'}</InputLabel>
        <Select
          label={isSpanish ? '¿Tienes dependientes?' : 'Do you have dependents?'}
          value={perfil.hasDependents}
          onChange={(e) => setPerfil((p) => ({ ...p, hasDependents: e.target.value }))}
        >
          <MenuItem value="no">{isSpanish ? 'No' : 'No'}</MenuItem>
          <MenuItem value="yes">{isSpanish ? 'Sí' : 'Yes'}</MenuItem>
        </Select>
      </FormControl>
      {perfil.hasDependents === 'yes' && (
        <TextField
          fullWidth
          type="number"
          label={isSpanish ? '¿Cuántos?' : 'How many?'}
          value={perfil.dependentsCount}
          onChange={(e) => setPerfil((p) => ({ ...p, dependentsCount: e.target.value }))}
        />
      )}
    </Box>
  );

  const renderCuentas = () => (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {isSpanish
          ? 'Registra al menos una cuenta bancaria o tarjeta para empezar a monitorear tu dinero real.'
          : 'Register at least one bank account or card to start monitoring your real money.'}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField
          label={isSpanish ? 'Nombre de la cuenta' : 'Account name'}
          placeholder={isSpanish ? 'Ej: Cuenta sueldo' : 'E.g. Paycheck account'}
          value={nuevaCuenta.nombre}
          onChange={(e) => setNuevaCuenta((c) => ({ ...c, nombre: e.target.value }))}
          sx={{ flex: '1 1 220px' }}
        />
        <Autocomplete
          freeSolo
          options={bancosSugeridos}
          value={nuevaCuenta.entidad}
          onInputChange={(e, value) => setNuevaCuenta((c) => ({ ...c, entidad: value }))}
          renderInput={(params) => <TextField {...params} label={isSpanish ? 'Banco / Entidad' : 'Bank / Institution'} />}
          sx={{ flex: '1 1 220px' }}
        />
        <FormControl sx={{ flex: '1 1 160px' }}>
          <InputLabel>{isSpanish ? 'Tipo' : 'Type'}</InputLabel>
          <Select
            label={isSpanish ? 'Tipo' : 'Type'}
            value={nuevaCuenta.tipo}
            onChange={(e) => setNuevaCuenta((c) => ({ ...c, tipo: e.target.value }))}
          >
            <MenuItem value="ahorros">{isSpanish ? 'Cuenta de Ahorros' : 'Savings Account'}</MenuItem>
            <MenuItem value="corriente">{isSpanish ? 'Cuenta Corriente' : 'Checking Account'}</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ flex: '1 1 120px' }}>
          <InputLabel>{isSpanish ? 'Moneda' : 'Currency'}</InputLabel>
          <Select
            label={isSpanish ? 'Moneda' : 'Currency'}
            value={nuevaCuenta.moneda}
            onChange={(e) => setNuevaCuenta((c) => ({ ...c, moneda: e.target.value }))}
          >
            {monedaOptions.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label={isSpanish ? 'Saldo actual' : 'Current balance'}
          type="number"
          value={nuevaCuenta.saldo}
          onChange={(e) => setNuevaCuenta((c) => ({ ...c, saldo: e.target.value }))}
          sx={{ flex: '1 1 140px' }}
        />
      </Box>
      <Button
        variant="outlined"
        onClick={handleAgregarCuenta}
        disabled={savingCuenta}
        startIcon={savingCuenta ? <CircularProgress size={16} /> : null}
        sx={{ mb: 2 }}
      >
        {savingCuenta ? (isSpanish ? 'Guardando...' : 'Saving...') : (isSpanish ? 'Agregar cuenta' : 'Add account')}
      </Button>

      {cuentasAgregadas.length > 0 && (
        <>
          <Divider sx={{ mb: 1 }} />
          <List dense>
            {cuentasAgregadas.map((c) => (
              <ListItem key={c.id}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <AccountBalanceIcon fontSize="small" sx={{ color: goldColors.main }} />
                </ListItemIcon>
                <ListItemText primary={c.nombre} secondary={`${c.entidad} — ${c.moneda} ${c.saldo}`} />
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Box>
  );

  const renderDeudas = () => (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {isSpanish
          ? 'Si tienes préstamos o tarjetas de crédito, regístralos acá — si no tienes ninguna, puedes continuar sin agregar nada.'
          : "If you have loans or credit cards, register them here — if you don't have any, you can continue without adding anything."}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField
          label={isSpanish ? 'Nombre' : 'Name'}
          placeholder={isSpanish ? 'Ej: Tarjeta BCP Visa' : 'E.g. Chase Visa Card'}
          value={nuevaDeuda.nombre}
          onChange={(e) => setNuevaDeuda((d) => ({ ...d, nombre: e.target.value }))}
          sx={{ flex: '1 1 220px' }}
        />
        <FormControl sx={{ flex: '1 1 180px' }}>
          <InputLabel>{isSpanish ? 'Tipo' : 'Type'}</InputLabel>
          <Select
            label={isSpanish ? 'Tipo' : 'Type'}
            value={nuevaDeuda.tipo}
            onChange={(e) => setNuevaDeuda((d) => ({ ...d, tipo: e.target.value }))}
          >
            {tiposDeuda.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label={isSpanish ? 'Monto' : 'Amount'}
          type="number"
          value={nuevaDeuda.monto}
          onChange={(e) => setNuevaDeuda((d) => ({ ...d, monto: e.target.value }))}
          sx={{ flex: '1 1 120px' }}
        />
        <FormControl sx={{ flex: '1 1 110px' }}>
          <InputLabel>{isSpanish ? 'Moneda' : 'Currency'}</InputLabel>
          <Select
            label={isSpanish ? 'Moneda' : 'Currency'}
            value={nuevaDeuda.moneda}
            onChange={(e) => setNuevaDeuda((d) => ({ ...d, moneda: e.target.value }))}
          >
            {monedaOptions.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label={isSpanish ? 'Tasa interés anual % (opcional)' : 'Annual interest rate % (optional)'}
          type="number"
          value={nuevaDeuda.tasa_interes}
          onChange={(e) => setNuevaDeuda((d) => ({ ...d, tasa_interes: e.target.value }))}
          sx={{ flex: '1 1 160px' }}
        />
        <TextField
          label={isSpanish ? 'Plazo en meses (opcional)' : 'Term in months (optional)'}
          type="number"
          value={nuevaDeuda.plazo_meses}
          onChange={(e) => setNuevaDeuda((d) => ({ ...d, plazo_meses: e.target.value }))}
          sx={{ flex: '1 1 160px' }}
        />
      </Box>
      {cuotaEstimada != null && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {isSpanish ? 'Cuota mensual estimada' : 'Estimated monthly payment'}: {nuevaDeuda.moneda} {cuotaEstimada.toFixed(2)}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          onClick={handleAgregarDeuda}
          disabled={savingDeuda}
          startIcon={savingDeuda ? <CircularProgress size={16} /> : null}
        >
          {savingDeuda ? (isSpanish ? 'Guardando...' : 'Saving...') : (isSpanish ? 'Agregar deuda' : 'Add debt')}
        </Button>
        <Button
          variant="text"
          color="inherit"
          onClick={handleNoTengoDeudas}
          disabled={marcandoSinDeudas}
        >
          {marcandoSinDeudas ? (isSpanish ? 'Guardando...' : 'Saving...') : (isSpanish ? 'No tengo deudas, continuar' : "I don't have debts, continue")}
        </Button>
      </Box>

      {deudasAgregadas.length > 0 && (
        <>
          <Divider sx={{ mb: 1 }} />
          <List dense>
            {deudasAgregadas.map((d) => (
              <ListItem key={d.id}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircleIcon fontSize="small" color="success" />
                </ListItemIcon>
                <ListItemText primary={d.nombre} secondary={`${d.moneda} ${d.monto}`} />
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Box>
  );

  const renderListo = () => (
    <Box sx={{ textAlign: 'center', py: 3 }}>
      <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
      <Typography variant="h6" gutterBottom>{isSpanish ? '¡Listo!' : 'All set!'}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {isSpanish ? (
          <>
            Perfil completo, {cuentasAgregadas.length || 'tus'} cuenta(s) registrada(s)
            {sinDeudas || deudasAgregadas.length === 0
              ? ', sin deudas pendientes.'
              : ` y ${deudasAgregadas.length} deuda(s) registrada(s).`}
          </>
        ) : (
          <>
            Profile complete, {cuentasAgregadas.length || 'your'} account(s) registered
            {sinDeudas || deudasAgregadas.length === 0
              ? ', no pending debts.'
              : ` and ${deudasAgregadas.length} debt(s) registered.`}
          </>
        )}
      </Typography>
      <Button variant="contained" onClick={handleIrAlTablero} sx={{ bgcolor: goldColors.main, '&:hover': { bgcolor: goldColors.dark } }}>
        {isSpanish ? 'Ir al Tablero' : 'Go to Dashboard'}
      </Button>
    </Box>
  );

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="sm">
      <IconButton
        aria-label={isSpanish ? 'cerrar' : 'close'}
        onClick={onClose}
        sx={{ position: 'absolute', right: 8, top: 8 }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ p: 4 }}>
        {loadingInitial ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {step < 3 && (
              <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {step === 0 && renderPerfil()}
            {step === 1 && renderCuentas()}
            {step === 2 && renderDeudas()}
            {step === 3 && renderListo()}

            {step < 3 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  color="inherit"
                  disabled={step === 0}
                  onClick={() => setStep((s) => s - 1)}
                >
                  {isSpanish ? 'Atrás' : 'Back'}
                </Button>
                {step === 0 && (
                  <Button
                    variant="contained"
                    onClick={handleGuardarPerfil}
                    disabled={savingPerfil}
                    startIcon={savingPerfil ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    {savingPerfil ? (isSpanish ? 'Guardando...' : 'Saving...') : (isSpanish ? 'Siguiente' : 'Next')}
                  </Button>
                )}
                {step === 1 && (
                  <Button
                    variant="contained"
                    onClick={handleSiguienteCuentas}
                    disabled={cuentasAgregadas.length === 0 && !initialData?.accounts_completed}
                  >
                    {isSpanish ? 'Siguiente' : 'Next'}
                  </Button>
                )}
                {step === 2 && (
                  <Button
                    variant="contained"
                    onClick={handleFinalizarDeudas}
                    disabled={deudasAgregadas.length === 0 && !sinDeudas && !initialData?.debts_completed}
                  >
                    {isSpanish ? 'Finalizar' : 'Finish'}
                  </Button>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingWizard;
